import { useEffect, useMemo, useState } from "react";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import "../../components/table.css";
import "./McrBrands.css";

const { apiUrl } = config;
const MCR_FILTERS_URL = `${apiUrl}/mcr-api/products/filters`;

const McrBrands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pageInput, setPageInput] = useState("1");

    const getBrands = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await fetch(MCR_FILTERS_URL, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json();
            const list = Array.isArray(data?.brands) ? data.brands : [];
            list.sort((a, b) => (Number(b?.count) || 0) - (Number(a?.count) || 0));
            setBrands(list);
        } catch (err) {
            console.error("Fetch Error:", err);
            setError("Unable to load brands. Please try again.");
            setBrands([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getBrands();
    }, []);

    const totalProducts = useMemo(
        () => brands.reduce((sum, item) => sum + (Number(item?.count) || 0), 0),
        [brands]
    );

    const filteredBrands = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return brands;
        return brands.filter((item) =>
            (item?.brand || "").toLowerCase().includes(q)
        );
    }, [brands, search]);

    const totalPages = Math.max(1, Math.ceil(filteredBrands.length / pageSize));
    const page = Math.min(currentPage, totalPages);

    const pageRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredBrands.slice(start, start + pageSize);
    }, [filteredBrands, page, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
        setPageInput("1");
    }, [search]);

    const goToPage = (p, syncInput = true) => {
        if (p < 1 || p > totalPages) return;
        setCurrentPage(p);
        if (syncInput) setPageInput(String(p));
    };

    const commitPageInput = () => {
        const requestedPage = Number(pageInput);
        if (Number.isInteger(requestedPage) && requestedPage >= 1 && requestedPage <= totalPages) {
            goToPage(requestedPage);
        } else {
            setPageInput(String(page));
        }
    };

    return (
        <ThemeMaker>
            <div className="mcrb-page">
                <div className="mcrb-card">
                    <div className="mcrb-head">
                        <div>
                            <h3 className="mcrb-head-title">MCR Brands</h3>
                            <p className="mcrb-head-sub">
                                Master Catalogue Repository &middot; brand distribution
                            </p>
                        </div>
                        {!loading && !error && brands.length > 0 && (
                            <div className="mcrb-head-stats">
                                <div className="mcrb-stat">
                                    <span className="mcrb-stat-num">{brands.length.toLocaleString()}</span>
                                    <span className="mcrb-stat-label">Brands</span>
                                </div>
                                <div className="mcrb-stat">
                                    <span className="mcrb-stat-num">{totalProducts.toLocaleString()}</span>
                                    <span className="mcrb-stat-label">Products</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {!loading && !error && brands.length > 0 && (
                        <div className="mcrb-toolbar">
                            <div className="mcrb-search">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="M21 21l-4.3-4.3" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search brand&hellip;"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button
                                        type="button"
                                        className="mcrb-search-clear"
                                        onClick={() => setSearch("")}
                                        aria-label="Clear search"
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                            <span className="mcrb-result-count">
                                {filteredBrands.length.toLocaleString()}
                                {filteredBrands.length === 1 ? " brand" : " brands"}
                            </span>
                        </div>
                    )}

                    {loading ? (
                        <div className="mcrb-state">
                            <div className="mcrb-spinner" />
                            Loading brands&hellip;
                        </div>
                    ) : error ? (
                        <div className="mcrb-state mcrb-state--error">{error}</div>
                    ) : brands.length === 0 ? (
                        <div className="mcrb-state">No brands found.</div>
                    ) : (
                        <>
                            <div className="mcrb-scroll">
                                <table className="mcrb-table">
                                    <thead>
                                        <tr>
                                            <th className="mcrb-th-rank">S.No.</th>
                                            <th>Brand</th>
                                            <th className="mcrb-th-count">Products</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="mcrb-empty-row">
                                                    No brands match &ldquo;{search}&rdquo;
                                                </td>
                                            </tr>
                                        ) : (
                                            pageRows.map((item, index) => {
                                                const count = Number(item?.count) || 0;
                                                const name = item?.brand || "—";
                                                const initial = name.trim().charAt(0) || "—";
                                                const rank = (page - 1) * pageSize + index + 1;
                                                return (
                                                    <tr key={`${name}-${rank}`}>
                                                        <td>
                                                            <span className="mcrb-rank">{rank}</span>
                                                        </td>
                                                        <td>
                                                            <div className="mcrb-brand">
                                                                <span className="mcrb-badge">{initial}</span>
                                                                <span className="mcrb-brand-name">{name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="mcrb-count">
                                                            {count.toLocaleString()}
                                                            <span className="mcrb-count-unit">SKU</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {filteredBrands.length > 0 && (
                                <div className="my-3 d-flex flex-column justify-content-center align-items-center">
                                    <div className="d-flex justify-content-center align-items-center flex-wrap" style={{ fontSize: "12px" }}>
                                        <button
                                            type="button"
                                            onClick={() => goToPage(page - 1)}
                                            className="page_link_item"
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>{" "}
                                        <div className="d-flex align-items-center" style={{ fontSize: "12px" }}>
                                            <span className="page_link_item">
                                                Page <strong>{page} of {totalPages}</strong>
                                            </span>
                                            <span className="page_link_item">
                                                | Go to page:{" "}
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={totalPages}
                                                    value={pageInput}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, "");
                                                        setPageInput(value);
                                                        const requestedPage = Number(value);
                                                        if (value && requestedPage >= 1 && requestedPage <= totalPages) {
                                                            goToPage(requestedPage, false);
                                                        }
                                                    }}
                                                    onBlur={commitPageInput}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            commitPageInput();
                                                            e.currentTarget.blur();
                                                        }
                                                    }}
                                                    style={{ width: "50px" }}
                                                />
                                            </span>{" "}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => goToPage(page + 1)}
                                            className="page_link_item"
                                            disabled={page === totalPages}
                                        >
                                            Next
                                        </button>{" "}
                                        <select
                                            value={pageSize}
                                            className="page_link_item"
                                            onChange={(e) => {
                                                setPageSize(Number(e.target.value));
                                                setCurrentPage(1);
                                                setPageInput("1");
                                            }}
                                            aria-label="Rows per page"
                                        >
                                            {[20, 50, 100].map((size) => (
                                                <option key={size} value={size}>Show {size}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </ThemeMaker>
    );
};

export default McrBrands;
